'use client';

import { useEffect, useState } from 'react';

import { reloadOnChunkError } from '@/util/chunk-reload';

/**
 * App-level error boundary. Catches errors thrown during rendering,
 * including ChunkLoadErrors from Next.js 13's RSC chunk double-encoding
 * bug (dynamic routes like [slug] get %25-encoded during SPA navigation).
 *
 * When a ChunkLoadError is detected, reloads the page so the full
 * server-rendered version loads cleanly. Fixed in Next.js 14.
 */
export default function GlobalError({
  error,
  reset
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const isChunkError = error?.name === 'ChunkLoadError';
  const [reloadSuppressed, setReloadSuppressed] = useState(false);

  useEffect(() => {
    if (!isChunkError) return;
    // If the reload was suppressed (loop guard tripped or storage blocked),
    // fall through to the fallback UI instead of rendering a blank page.
    if (!reloadOnChunkError()) setReloadSuppressed(true);
  }, [isChunkError]);

  if (isChunkError && !reloadSuppressed) {
    // Page will reload; render nothing in the meantime
    return null;
  }

  return (
    <div>
      <h2>Something went wrong.</h2>
      <button onClick={reset}>Try again</button>
    </div>
  );
}
